import { 
    Language, 
    CreateLanguageRequest, 
    UpdateLanguageRequest, 
    LanguageDependencyCheck,
    LanguageFilters 
} from "@/types/language.types";
import { AxiosError } from "axios";
import axios from "@/axios.config";
import { ApiResponse } from "@/types/fetch.type";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

// Get all languages
export const useGetLanguages = (filters?: LanguageFilters) => {
    return useQuery<ApiResponse<Language[]>, AxiosError>({
        queryKey: ["languages", filters],
        queryFn: async () => {
            const params = new URLSearchParams();
            if (filters?.code) params.append('code', filters.code);
            if (filters?.enabled !== undefined) params.append('enabled', filters.enabled.toString());
            
            const url = filters?.code ? `/languages?${params}` : '/languages';
            const response = await axios.get<ApiResponse<Language[]>>(url);
            return response.data;
        },
    });
};

// Get enabled languages only
export const useGetEnabledLanguages = () => {
    return useQuery<ApiResponse<Language[]>, AxiosError>({
        queryKey: ["languages", "enabled"],
        queryFn: async () => {
            const response = await axios.get<ApiResponse<Language[]>>('/languages/enabled');
            return response.data;
        },
    });
};

// Get enabled languages for a specific council
export const useGetEnabledLanguagesByCouncil = (councilId?: string) => {
    console.log('useGetEnabledLanguagesByCouncil called with councilId:', councilId);
    return useQuery<ApiResponse<Language[]>, AxiosError>({
        queryKey: ["languages", "enabled", "council", councilId],
        queryFn: async () => {
            console.log('Fetching languages for councilId:', councilId);
            if (!councilId) {
                // Fallback to general enabled languages if no council ID
                console.log('No councilId, falling back to general enabled languages');
                const response = await axios.get<ApiResponse<Language[]>>('/languages/enabled');
                console.log('General enabled languages response:', response.data);
                return response.data;
            }
            console.log('Fetching council-specific languages from:', `/councils/${councilId}/languages/enabled`);
            const response = await axios.get<ApiResponse<Language[]>>(`/councils/${councilId}/languages/enabled`);
            console.log('Council-specific languages response:', response.data);
            return response.data;
        },
        enabled: true, // Always enable the query
    });
};

// Get language by ID
export const useGetLanguageById = (id?: string) => {
    return useQuery<ApiResponse<Language>, AxiosError>({
        queryKey: ["languages", id],
        queryFn: async () => {
            const response = await axios.get<ApiResponse<Language>>(`/languages/${id}`);
            return response.data;
        },
        enabled: !!id,
    });
};

// Create language
export const useCreateLanguage = () => {
    const queryClient = useQueryClient();
    return useMutation<ApiResponse<Language>, AxiosError<{ message: string }>, CreateLanguageRequest>({
        mutationFn: async (data) => {
            const response = await axios.post<ApiResponse<Language>>('/languages', data);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["languages"] });
        },
    });
};

// Update language
export const useUpdateLanguage = () => {
    const queryClient = useQueryClient();
    return useMutation<ApiResponse<Language>, AxiosError<{ message: string }>, { id: string; data: UpdateLanguageRequest }>({
        mutationFn: async ({ id, data }) => {
            const response = await axios.put<ApiResponse<Language>>(`/languages/${id}`, data);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["languages"] });
        },
    });
};

// Delete language
export const useDeleteLanguage = () => {
    const queryClient = useQueryClient();
    return useMutation<ApiResponse<void>, AxiosError<{ message: string }>, string>({
        mutationFn: async (id) => {
            const response = await axios.delete<ApiResponse<void>>(`/languages/${id}`);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["languages"] });
        },
    });
};

// Check language dependencies
export const useCheckLanguageDependencies = (code?: string) => {
    return useQuery<ApiResponse<LanguageDependencyCheck>, AxiosError>({
        queryKey: ["languages", "dependencies", code],
        queryFn: async () => {
            const response = await axios.get<ApiResponse<LanguageDependencyCheck>>(`/languages/${code}/dependencies`);
            return response.data;
        },
        enabled: !!code,
    });
};
